import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CookieConsentContext = createContext(null);

const STORAGE_KEY = 'vwc_cookie_consent';

const DEFAULT_PREFERENCES = {
  essential: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showMiniBadge, setShowMiniBadge] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setConsent(parsed);
        setShowBanner(false);
        setShowMiniBadge(false);
      } catch {
        setShowBanner(true);
      }
    } else {
      setShowBanner(true);
    }
  }, []);

  const saveConsent = useCallback((prefs) => {
    const data = {
      ...prefs,
      essential: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setConsent(data);
    setShowBanner(false);
    setShowMiniBadge(false);
    setShowSettings(false);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ essential: true, analytics: true, marketing: true, preferences: true });
  }, [saveConsent]);

  const rejectNonEssential = useCallback(() => {
    saveConsent({ ...DEFAULT_PREFERENCES });
  }, [saveConsent]);

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
    setShowMiniBadge(true);
  }, []);

  const reopenBanner = useCallback(() => {
    setShowMiniBadge(false);
    setShowBanner(true);
  }, []);

  const openSettings = useCallback(() => {
    setShowBanner(false);
    setShowMiniBadge(false);
    setShowSettings(true);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
    if (!consent) {
      setShowMiniBadge(true);
    }
  }, [consent]);

  const hasConsent = useCallback((category) => {
    if (!consent) return category === 'essential';
    return !!consent[category];
  }, [consent]);

  return (
    <CookieConsentContext.Provider value={{
      consent,
      showBanner,
      showMiniBadge,
      showSettings,
      acceptAll,
      rejectNonEssential,
      saveConsent,
      openSettings,
      closeSettings,
      dismissBanner,
      reopenBanner,
      hasConsent,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be inside CookieConsentProvider');
  return ctx;
};
